'use client';
import React, { useState, forwardRef, Ref, useEffect, useRef } from 'react';
import styles from './menulist.module.sass';
import cn from 'classnames';
import { Separator } from '../Separator';
import { FontIcon, FontIconName } from '../FontIcon';
import { Text } from '../Text';
import { Button } from '../Button';

export interface MenuListProps extends React.HTMLAttributes<HTMLDivElement> {
    triggerTitle: string | React.ReactNode;
    triggerIcon?: FontIconName;
    children?: React.ReactElement<MenuItemProps>[] | React.ReactElement<MenuItemProps>;
    width?: 'full' | 'fit-content';
    variant?: 'text' | 'select' | 'drop' | 'placeholder';
    dropIcon?: FontIconName;
    align?: 'start' | 'end';
    border?: boolean;
    onChange?: (value: any) => void;
    compact?: boolean;
    selectedItem?: any;
    above?: boolean;
    placeholder?: string;
    className?: string;
}
type childProps = {
    children: any;
};
export const MenuList = forwardRef(
    (
        {
            triggerTitle,
            children,
            width,
            variant = 'select',
            dropIcon,
            align,
            triggerIcon,
            border,
            onChange,
            compact,
            className,
            selectedItem,
            above,
            placeholder,
            ...props
        }: MenuListProps,
        ref?: Ref<HTMLDivElement>
    ) => {
        const [isOpen, setIsOpen] = useState(false);
        const [selectedItemState, setSelectedItem] = useState<any>(selectedItem);
        const [selectedText, setSelectedText] = useState<string | React.ReactNode>(triggerTitle);
        const menuRef = useRef<HTMLDivElement | null>(null);
        useEffect(() => {
            setSelectedItem(selectedItem);
        }, [selectedItem]);

        useEffect(() => {
            if (variant !== 'drop') {
                const selectedChild = React.Children.toArray(children).find(
                    (child) => React.isValidElement<MenuItemProps>(child) && child.props.value === selectedItemState
                );

                if (React.isValidElement<{ children: any }>(selectedChild)) {
                    setSelectedText(selectedChild.props.children);
                } else {
                    setSelectedText(triggerTitle);
                }
            }
            setSelectedText(triggerTitle);
            setIsOpen(false);
        }, [selectedItemState, children, triggerTitle, variant]);

        const toggleDropdown = () => setIsOpen(!isOpen);

        const handleItemClick = (value: any, childText?: string | React.ReactNode) => {
            setSelectedItem(value);

            // Only update the dropdown item's selected state but don't change triggerTitle for 'drop' variant
            if (variant !== 'drop' && dropIcon) {
                setSelectedText(childText ?? triggerTitle);
            }
            if (onChange) {
                onChange(value);
            }

            setIsOpen(false);
        };

        useEffect(() => {
            const handleOutsideClick = (event: MouseEvent) => {
                if (menuRef?.current && !menuRef.current.contains(event.target as Node)) {
                    setIsOpen(false);
                }
            };
            document.addEventListener('mousedown', handleOutsideClick);
            return () => document.removeEventListener('mousedown', handleOutsideClick);
        }, []);

        const renderChild = React.Children.map(children, (child) => {
            if (React.isValidElement(child) && 'value' in child.props) {
                const originalOnClick = child.props.onClick;
                return React.cloneElement(child, {
                    onClick: (e: React.SyntheticEvent) => {
                        if (originalOnClick) {
                            originalOnClick(e);
                        }
                        handleItemClick(child.props.value, child.props.children);
                    },
                    selected: child.props.value === selectedItemState,
                });
            }
            return child;
        });

        return (
            <div
                className={cn(
                    className,
                    styles.holder,
                    styles[width ? width : ''],
                    align && styles[`align-${align}`],
                    border && styles.border
                )}
                {...props}
                ref={(node) => {
                    menuRef.current = node;
                    if (typeof ref === 'function') {
                        ref(node);
                    } else if (ref && 'current' in ref) {
                        (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
                    }
                }}
            >
                <div
                    className={cn(
                        variant === 'select' ? styles.title : variant === 'drop' ? styles.drop : styles.text,
                        // variant,
                        compact && styles.compact,
                        isOpen && styles.open
                    )}
                    role="button"
                    onClick={toggleDropdown}
                    tabIndex={0}
                    aria-haspopup="true"
                    aria-expanded={isOpen}
                >
                    {triggerIcon && (
                        <div className={styles.frame}>
                            <FontIcon name={triggerIcon} size="smaller" md="smaller" />
                            <span className={cn(styles.heading, compact && styles.compact)}>{selectedText}</span>
                        </div>
                    )}
                    {!triggerIcon && typeof triggerTitle == 'string' && <span>{selectedText}</span>}
                    {!triggerIcon && typeof triggerTitle != 'string' && selectedText}
                    {dropIcon && (
                        <FontIcon
                            name={dropIcon}
                            size="smaller"
                            md="smaller"
                            {...(dropIcon === 'arrow' ? { rotate: 'left' } : {})}
                        />
                    )}
                </div>
                {isOpen && !placeholder && (
                    <div className={cn(styles.list, above && styles.above, placeholder && styles.placeholder)}>
                        {renderChild}
                    </div>
                )}
                {placeholder && (
                    <div className={cn(styles.fixed, isOpen && styles.choose)}>
                        <div className={styles.header}>
                            <Text as="h3" variant="h3" alignment="start" margin="0" size="16">
                                {placeholder}
                            </Text>
                            <Button
                                className={styles.close}
                                compact
                                social
                                secondary
                                round
                                onClick={() => setIsOpen(false)}
                            >
                                <FontIcon name="close" />
                            </Button>
                        </div>
                        <div className={cn(styles.list, placeholder && styles.placeholder)}>{renderChild}</div>
                    </div>
                )}
            </div>
        );
    }
);

interface MenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
    onClick?: (event: React.SyntheticEvent) => void;
    children?: React.ReactNode;
    divider?: boolean;
    selected?: boolean;
    variant?: 'text' | 'link';
    linkAs?: React.ElementType;
    href?: string;
    linkProps?: { [key: string]: any };
    value?: any;
    to?: string;
}

export const MenuItem = ({
    onClick,
    children,
    divider,
    selected,
    variant = 'text',
    linkAs: LinkComponent,
    href,
    linkProps,
    className,
    ...props
}: MenuItemProps) => {
    if (divider) {
        return <Separator />;
    }

    const classNames = cn(className, styles.item, { [styles.selected]: selected });

    if (variant === 'link') {
        const LinkWrapper = LinkComponent || 'a';
        const additionalProps = LinkComponent ? { ...linkProps } : { href: href };

        return (
            <LinkWrapper className={classNames} onClick={onClick} {...additionalProps} {...props}>
                {children}
                {selected && <FontIcon name="check-green" size="smaller" md="smaller" />}
            </LinkWrapper>
        );
    } else {
        return (
            <div className={classNames} onClick={onClick} {...props}>
                {children}
                {selected && <FontIcon name="check-green" size="smaller" md="smaller" />}
            </div>
        );
    }
};
